import React from 'react';
import useGigsFilters from '@/hooks/use-gigs-filters';
import useVerticalTagsQuery from '@/hooks/queries/use-vertical-tags-query';
import GigsFiltersPopup from './gigs-filters-popup';
import useInvitedGigsQuery from '@/hooks/queries/gigs/use-invited-gigs-query';
import useGenericButtonPressedEvent from '@/hooks/analytics/use-generic-button-pressed-event';
import {screenNames, buttonNames} from '@/lib/delta/deltaConstants';

const SCROLL_TO_TOP_OFFSET = 0;
const SCROLL_TO_TOP_WITH_INVITED_GIGS_OFFSET = 600;

/**
 * Wrapper component that connects the GigsFiltersPopup to the GigsStore
 * Handles interactions with the URL and store, and passes data as props to GigsFiltersPopup
 */
function GigsFiltersPopupWrapper() {
  const {
    showFiltersModal,
    closeFiltersModal,
    draftFilters,
    updateFilter,
    applyFilters,
    resetFilters,
    draftFilterResultsCount,
    isCountLoading,
  } = useGigsFilters();
  const {pressed} = useGenericButtonPressedEvent();

  // Use the custom hook for vertical tags
  const {data, isLoading: isLoadingVerticalTags} = useVerticalTagsQuery();
  const verticalTags = data?.data || [];

  const {count: invitedGigsCount} = useInvitedGigsQuery({previewFetch: true});
  const scrollToTopOffset =
    invitedGigsCount > 0
      ? SCROLL_TO_TOP_WITH_INVITED_GIGS_OFFSET
      : SCROLL_TO_TOP_OFFSET;

  const handleApplyFilters = () => {
    pressed({
      button_name: buttonNames.suggested_gigs.apply_filters,
      screen_name: screenNames.suggested_gigs.filters_popup,
    });
    const areFiltersChanged = applyFilters();
    if (areFiltersChanged) {
      // Scroll to top of the screen after search is completed
      window.scrollTo({top: scrollToTopOffset, behavior: 'smooth'});
    }
  };

  const handleResetFilters = () => {
    pressed({
      button_name: buttonNames.suggested_gigs.reset_filters,
      screen_name: screenNames.suggested_gigs.filters_popup,
    });
    const areFiltersReset = resetFilters();
    if (areFiltersReset) {
      // Scroll to top of the screen after reset is completed
      window.scrollTo({top: scrollToTopOffset, behavior: 'smooth'});
    }
  };

  const handleCloseFilters = () => {
    pressed({
      button_name: buttonNames.suggested_gigs.close_filters,
      screen_name: screenNames.suggested_gigs.filters_popup,
    });
    closeFiltersModal();
  };

  return (
    <GigsFiltersPopup
      isOpen={showFiltersModal}
      onClose={handleCloseFilters}
      filters={draftFilters}
      onFilterChange={updateFilter}
      onApplyFilters={handleApplyFilters}
      onResetFilters={handleResetFilters}
      filterResultsCount={draftFilterResultsCount}
      isCountLoading={isCountLoading}
      verticalTags={verticalTags}
      isLoadingVerticalTags={isLoadingVerticalTags}
    />
  );
}

export default GigsFiltersPopupWrapper;
